import { DestroyRef, Directive, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { ProviderShell } from './provider-shell';
import { ProviderConfig } from '../models/provider.model';
import { ProviderConfigService } from '../services/provider-config.service';
import { GridColumn, GridRowAction } from '../../../shared/components/grid/models/grid.model';
import { LanguageService } from '../../../core/i18n/language.service';
import { LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { ModalService } from '../../../shared/components/modal/modal.service';
import { ToastService } from '../../../shared/services/toast-service';
import { effectiveFeedStatus, feedStatusDot } from '../util/feed-status.util';

/**
 * Base for the per-type Feeds tabs. Each subclass is rendered in its shell's
 * outlet and supplies its own columns/template; this holds what every type's
 * feed grid shares: the shell's feed list with client-side search (the whole
 * per-provider list is already fetched — no server paging), the Feed/Tenant/
 * Status columns, row click → feed page, and the Delete row action.
 */
@Directive()
export abstract class ProviderFeedsBase {
  protected readonly shell = inject(ProviderShell);
  protected readonly configService = inject(ProviderConfigService);
  protected readonly modal = inject(ModalService);
  protected readonly toastService = inject(ToastService);
  protected readonly transloco = inject(TranslocoService);
  protected readonly languageService = inject(LanguageService);
  protected readonly router = inject(Router);
  protected readonly destroyRef = inject(DestroyRef);

  readonly isLoading = this.shell.isFeedsLoading;
  readonly tenantNameById = this.shell.tenantNameById;

  readonly searchTerm = signal('');

  /** Extra per-feed text the search matches on top of name/tenant/locality. */
  protected searchableText(_feed: ProviderConfig): string[] {
    return [];
  }

  readonly feeds = computed<ProviderConfig[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.shell.feeds();
    const names = this.tenantNameById();
    return this.shell.feeds().filter((feed) =>
      [feed.name, names.get(feed.tenantId) ?? '', feed.locality ?? '', ...this.searchableText(feed)].some((text) =>
        text.toLowerCase().includes(term),
      ),
    );
  });

  /** Stable row id for tracking. */
  readonly feedId = (feed: ProviderConfig) => feed.id;
  /** Narrows the erased-generic row that grid events hand back. */
  readonly asFeed = (row: unknown): ProviderConfig => row as ProviderConfig;

  readonly primaryAction = computed(() => ({
    label: this.transloco.translate('providers.feedsGrid.addFeed'),
    icon: 'icon-content-add24px' as const,
  }));

  /** Delete as a row shortcut — skips opening the feed page just to click its
   *  own Delete feed button. */
  readonly rowActions = computed<GridRowAction<ProviderConfig>[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate(`providers.feedsGrid.${k}`);
    return [{ id: 'delete', label: t('deleteAction'), icon: 'icon-delete-trash', placement: 'menu', danger: true }];
  });

  /** Leading Feed (name + subtitle template cell) and Tenant columns. Call
   *  inside a computed that already reads `languageService.current()`. */
  protected leadingColumns(): GridColumn<ProviderConfig>[] {
    const t = (k: string) => this.transloco.translate(`providers.feedsGrid.columns.${k}`);
    return [
      { key: 'name', label: t('feed'), type: 'template', template: 'name', sortable: false, alwaysVisible: true },
      {
        key: 'tenantId',
        label: t('tenant'),
        type: 'text',
        sortable: false,
        value: (row) => this.tenantNameById().get(row.tenantId) ?? row.tenantId,
      },
    ];
  }

  /** The provider's verdict on the feed configuration (template cell 'status'). */
  protected statusColumn(): GridColumn<ProviderConfig> {
    const label = this.transloco.translate('providers.feedsGrid.columns.status');
    return { key: 'status', label, type: 'template', template: 'status', width: 150, sortable: false };
  }

  statusDot(feed: ProviderConfig): LabelBadgeDot {
    return feedStatusDot(effectiveFeedStatus(feed));
  }

  statusLabel(feed: ProviderConfig): string {
    return this.transloco.translate(`providers.feedPage.status.${effectiveFeedStatus(feed)}`);
  }

  /** The provider's rejection reason, only while that rejection is current. */
  statusMessage(feed: ProviderConfig): string | null {
    return effectiveFeedStatus(feed) === 'REJECTED' ? feed.statusMessage : null;
  }

  /** "N stops · locality", or just "N stops" when no locality is set. */
  subtitle(feed: ProviderConfig): string {
    return feed.locality
      ? this.transloco.translate('providers.feedsGrid.subtitleStopsWithLocality', { count: feed.resourceCount, locality: feed.locality })
      : this.transloco.translate('providers.feedsGrid.subtitleStops', { count: feed.resourceCount });
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
  }

  onRowClick(feed: ProviderConfig): void {
    void this.router.navigate([...this.shell.basePath(), feed.id]);
  }

  onRowAction(actionId: string, feed: ProviderConfig): void {
    if (actionId === 'delete') {
      void this.confirmDelete(feed);
    }
  }

  addFeed(): void {
    this.shell.addFeed();
  }

  private async confirmDelete(feed: ProviderConfig): Promise<void> {
    const confirmed = await this.modal.confirm({
      kind: 'destructive',
      destructive: true,
      title: this.transloco.translate('providers.feedsGrid.delete.title'),
      message: this.transloco.translate('providers.feedsGrid.delete.message', { name: feed.name }),
      confirmLabel: this.transloco.translate('providers.feedsGrid.delete.confirm'),
      confirmWord: feed.name,
    });
    if (!confirmed) return;

    this.configService
      .delete(feed.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show(this.transloco.translate('providers.feedsGrid.toast.deleted'), 'success');
          this.shell.refetchFeeds();
        },
        error: () => this.toastService.show(this.transloco.translate('providers.feedsGrid.toast.deleteFailed'), 'error'),
      });
  }
}
